// One dictionary for the whole web app (solo results and multiplayer review):
// Merriam-Webster first, then AI for words it doesn't know (names, places,
// brands), then Wikipedia. Every step has a timeout so a slow or down source
// can't leave a lookup spinning. Mirrors the mobile app's DictionaryLookup.tsx
// (Merriam-Webster, then AI through the same Cloud Function).
import { getCategoryMatch } from "./categoryMatch";
import { groqChatCompletion } from "./aiProxy";

const MW_KEY = "9155b173-0376-4480-a6b4-7a5fc919a695";
const TIMEOUT_MS = 6000;

function withTimeout(promise, ms, onTimeout) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => {
        onTimeout?.();
        reject(new Error("timeout"));
      }, ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

function timedFetch(url, fetchImpl, ms) {
  const ctrl = new AbortController();
  return withTimeout(fetchImpl(url, { signal: ctrl.signal }), ms, () => ctrl.abort());
}

// Returns an entry, null when the word isn't in Merriam-Webster; throws when unreachable.
async function fromMerriamWebster(word, { fetchImpl, timeoutMs }) {
  const res = await timedFetch(
    `https://www.dictionaryapi.com/api/v3/references/collegiate/json/${encodeURIComponent(word.toLowerCase())}?key=${MW_KEY}`,
    fetchImpl,
    timeoutMs
  );
  if (!res.ok) throw new Error("mw_error");
  const data = await res.json();
  // Unknown words come back as an array of spelling suggestions (strings).
  if (!data.length || typeof data[0] === "string" || !data[0].shortdef?.[0]) return null;
  const entry = data[0];
  let example = null;
  const dt = entry.def?.[0]?.sseq?.[0]?.[0]?.[1]?.dt;
  const vis = dt?.find((item) => item[0] === "vis");
  if (vis?.[1]?.[0]?.t) example = vis[1][0].t.replace(/\{.*?\}/g, "");
  return {
    word: entry.meta?.id?.split(":")[0] || word,
    phonetic: entry.hwi?.prs?.[0]?.mw || "",
    partOfSpeech: entry.fl || "",
    definition: entry.shortdef[0],
    example,
    source: "dictionary",
  };
}

async function fromAI(word, category, { aiImpl, timeoutMs }) {
  const catStr = category ? ` (category: ${category})` : "";
  const content = await withTimeout(
    aiImpl({
      model: "qwen/qwen3.8-27b",
      messages: [
        { role: "system", content: "You are a helpful dictionary assistant. Respond concisely in JSON." },
        {
          role: "user",
          content: `Provide a concise dictionary-style JSON response for the word "${word}"${catStr}. Return only a JSON object with these fields: word, partOfSpeech (if known), definition (one or two sentences), example (one short sentence). If the word is not real, set definition to null.`,
        },
      ],
      temperature: 0.2,
      max_tokens: 400,
      reasoning_effort: "none",
    }),
    timeoutMs
  );
  const match = String(content || "").match(/\{[\s\S]*\}/);
  if (!match) return null;
  const parsed = JSON.parse(match[0]);
  if (!parsed.definition) return null;
  return {
    word: parsed.word || word,
    phonetic: "",
    partOfSpeech: parsed.partOfSpeech || "",
    definition: parsed.definition,
    example: parsed.example || null,
    source: "ai",
  };
}

async function fromWikipedia(word, { fetchImpl, timeoutMs }) {
  const res = await timedFetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(word)}`, fetchImpl, timeoutMs);
  if (!res.ok) return null;
  const data = await res.json();
  if (data.type === "disambiguation" || !data.extract) return null;
  return { word, phonetic: "", partOfSpeech: data.description || "", definition: data.extract, example: null, source: "wikipedia" };
}

/**
 * Resolves to { word, phonetic, partOfSpeech, definition, example, source, categoryMatch }.
 * Throws Error("not_found") when no source knows the word, or
 * Error("unavailable") when no source could be reached.
 */
export async function lookupWord(word, { category, fetchImpl = fetch, aiImpl = groqChatCompletion, timeoutMs = TIMEOUT_MS } = {}) {
  const clean = String(word || "").trim();
  if (!clean) throw new Error("not_found");
  const steps = [
    () => fromMerriamWebster(clean, { fetchImpl, timeoutMs }),
    () => fromAI(clean, category, { aiImpl, timeoutMs }),
    () => fromWikipedia(clean, { fetchImpl, timeoutMs }),
  ];
  let reached = false;
  for (const step of steps) {
    try {
      const entry = await step();
      reached = true;
      if (entry) return { ...entry, categoryMatch: getCategoryMatch(entry.definition, entry.partOfSpeech, entry.word, category) };
    } catch {
      // Unreachable or timed out: try the next source.
    }
  }
  throw new Error(reached ? "not_found" : "unavailable");
}
